/**
 * Pure utilities for durable HITL gate card rendering.
 *
 * Durable rendering lets the in-chat gate card survive a hard refresh, a
 * missed WebSocket event, or a conversation switch by seeding the card from
 * the task list instead of relying exclusively on the live WS push.
 *
 * All functions are framework-free and unit-testable with plain Node.
 */

/** Block types that can render as an in-chat gate card. */
const RENDERABLE_BLOCK_TYPES = new Set([
  'hitl_approval',
  'hitl_qa',
  'hitl_choice',
  'hitl_pick',
]);

const isCardEligible = (task) =>
  Boolean(task?.render?.ui_block && RENDERABLE_BLOCK_TYPES.has(task.render.ui_block));

/** The conversation a task's gate belongs to, or '' when the seed left it untagged. */
const taskConversationUuid = (task) =>
  task?.render?.conversation_uuid ?? task?.render?.conversationUuid ?? '';

/** Newest task by createdAt (ISO strings compare lexicographically here). */
const newestByCreatedAt = (tasks) =>
  tasks.reduce((best, t) => {
    if (!best) return t;
    return (t.createdAt ?? '') > (best.createdAt ?? '') ? t : best;
  }, null);

/**
 * Pick the best pending HITL task to display as an in-chat gate card.
 *
 * Once a conversationId is known only a task whose render.conversation_uuid
 * equals it is eligible (newest wins). A task tagged for a DIFFERENT
 * conversation is never surfaced, and neither is an *untagged* one: it belongs
 * to no conversation we can prove, so offering it would paint another chat's
 * approval card here. (A LangGraph tool approval used to be raised untagged, so
 * an email approval from one chat showed up in every other conversation that had
 * no card of its own. The producers now tag their tasks; an untagged task is
 * still reachable from the task inbox, just not from an arbitrary chat.)
 *
 * With no conversationId the newest eligible task is returned (pure-fn safety;
 * the hook always passes one).
 *
 * Returns null when no eligible task exists.
 *
 * `isDismissed` (injected, keeps this pure) drops contracts already proven dead
 * this session — a terminated workflow leaves its task `pending` forever, so
 * without it seeding would resurrect the same zombie card on every poll.
 *
 * @param {object[]} tasks  Raw pending task rows from listHitlTasks.
 * @param {{ conversationId?: string, isDismissed?: (contractId: string) => boolean }} [opts]
 * @returns {object|null}
 */
export const selectDurableHitlTask = (
  tasks,
  { conversationId, isDismissed } = {},
) => {
  if (!Array.isArray(tasks) || tasks.length === 0) return null;

  const eligible = tasks.filter(
    (t) => isCardEligible(t) && !isDismissed?.(t.contractId),
  );
  if (eligible.length === 0) return null;

  if (conversationId) {
    const exact = eligible.filter(
      (t) => taskConversationUuid(t) === conversationId,
    );
    return exact.length ? newestByCreatedAt(exact) : null;
  }

  return newestByCreatedAt(eligible);
};

/**
 * Build a synthetic chat.response.input.required payload from a pending task
 * row so the existing mapInputRequiredToGateCard mapper renders it unchanged.
 *
 * The shape mirrors what useChatStreaming.handleInputRequired receives from
 * the WebSocket — the card component does not know the difference.
 *
 * @param {object} task  A raw pending task row from listHitlTasks.
 * @returns {object}
 */
export const buildSyntheticInputRequired = (task) => ({
  interaction_id: task.contractId,
  status: 'PENDING',
  expires_at: task.expiresAt ?? null,
  created_at: task.createdAt ?? null,
  ui_block: {
    block_id: task.contractId,
    block_type: task.render?.ui_block ?? 'hitl_approval',
    placement: 'inline',
    data: task.render ?? {},
  },
  resume_match: task.resumeMatch ?? {},
});

/**
 * Return false when a displayed card's contract is no longer in the pending
 * task list — i.e. it was resolved elsewhere (inbox, script, another device).
 *
 * When hitlRequest is null there is no card shown, so return true to skip
 * reconciliation.
 *
 * A contract on the session denylist (`isDismissed`) is treated as no longer
 * pending even if the row still says so — that is the zombie case a resolve
 * attempt already proved dead (pending task != live workflow).
 *
 * @param {object|null} hitlRequest  Current hitlRequest slot value.
 * @param {object[]} pendingTasks   Rows returned by listHitlTasks (pending).
 * @param {{ isDismissed?: (contractId: string) => boolean }} [opts]
 * @returns {boolean}
 */
export const isCardStillPending = (hitlRequest, pendingTasks, { isDismissed } = {}) => {
  if (!hitlRequest) return true;
  const contractId = hitlRequest.interaction_id ?? hitlRequest.interactionId;
  if (!contractId) return false;
  if (isDismissed?.(contractId)) return false;
  return (
    Array.isArray(pendingTasks) &&
    pendingTasks.some((t) => t.contractId === contractId)
  );
};
